import React, { useState } from "react";
import { useSpamGuard } from "@/lib/spamGuard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Mail, MapPin, Heart } from "lucide-react";
import Mandala from "@/components/Mandala";
import SEO from "@/components/SEO";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/nk1vssa4_SEDONARETREAT-52.jpg";

const SUBJECTS = [
  "General question",
  "Retreats & travel",
  "Teacher trainings",
  "Reiki & 1-on-1 sessions",
  "Studio partnership",
  "Press / collaboration",
  "Other",
];

export default function Contact() {
  const { honeypot, guard } = useSpamGuard();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: SUBJECTS[0],
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      toast.error("Please share a few words so Holly knows how to help.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/inquiries/contact", { ...form, ...guard() });
      setDone(true);
      toast.success("Message sent — Holly will be in touch.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="pt-40 pb-24 min-h-[80vh]" data-testid="contact-thanks">
        <div className="container-aviana max-w-2xl text-center">
          <span className="ornament-flower mx-auto block" />
          <p className="overline mt-8 mb-4">Message received</p>
          <h1 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
            Thank you, <em className="italic font-serif gold-text">beautiful soul.</em>
          </h1>
          <p className="mt-8 text-aviana-muted leading-[1.85] font-serif italic text-lg">
            Holly reads every message personally and will respond within three days. While you wait,
            wander through the journal — there's always something new being woven.
          </p>
          <a href="/journal" className="btn-secondary mt-10 inline-flex" data-testid="contact-thanks-journal">
            Visit the Journal
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Holly Johnson · Aviana Yoga"
        description="Reach out about aerial yoga teacher trainings, women's retreats, Reiki healing, or studio partnerships. Holly Johnson personally reads every message and responds within 3 days."
        path="/contact"
        keywords="contact Holly Johnson, Aviana Yoga contact, aerial yoga training inquiry, retreat booking"
      />
      {/* Hero — image on right, text on left, matches Holly's old WP design */}
      <section className="pt-36 pb-16 md:pb-20 bg-aviana-cream relative overflow-hidden">
        <div className="absolute -top-20 -right-20 opacity-15 pointer-events-none">
          <Mandala size={520} color="#7E3F8F" />
        </div>
        <div className="container-aviana relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <p className="overline mb-5">Get in touch</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.98] text-aviana-ink">
              Let's <em className="italic font-serif gold-text">begin</em> a<br />
              conversation.
            </h1>
            <p className="mt-9 max-w-xl text-aviana-muted leading-[1.8] font-serif italic text-[20px] md:text-[22px]">
              Whether you're called to a retreat, curious about training, or simply want to say hello —
              this is the door. Holly reads each message herself.
            </p>
            <p className="mt-7 font-display text-2xl md:text-3xl text-aviana-plum italic">
              Many Blessings of Love, Light &amp; Flight.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-aviana-gold/30 aspect-[4/5] shadow-2xl shadow-aviana-plumDeep/20" data-testid="contact-hero-img">
              <img src={HERO_IMG} alt="Holly Johnson in Sedona" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-aviana-plumDeep/15 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Form + side image — image is the visual anchor next to the form */}
      <section className="py-20 md:py-24">
        <div className="container-aviana grid lg:grid-cols-12 gap-10 lg:gap-14 max-w-6xl">
          {/* Left: contact details — image lives in hero only (avoid redundancy) */}
          <div className="lg:col-span-5 space-y-8">
            <div className="rounded-[2rem] border border-aviana-border bg-aviana-cream p-9 md:p-10 relative overflow-hidden" data-testid="contact-info-card">
              <div className="absolute -top-10 -right-10 opacity-10 pointer-events-none">
                <Mandala size={200} color="#7E3F8F" />
              </div>
              <div className="space-y-6 relative">
                <div className="flex gap-4">
                  <Mail size={18} className="text-aviana-gold shrink-0 mt-1" />
                  <div>
                    <p className="overline mb-1">Email</p>
                    <a href="mailto:holly@avianayoga.com" className="text-aviana-ink hover:text-aviana-plum font-serif text-[17px]" data-testid="contact-email-link">
                      holly@avianayoga.com
                    </a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <MapPin size={18} className="text-aviana-gold shrink-0 mt-1" />
                  <div>
                    <p className="overline mb-1">Based in</p>
                    <p className="text-aviana-ink font-serif text-[17px]">Sugar Grove, IL · Travels worldwide</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Heart size={18} className="text-aviana-gold shrink-0 mt-1" />
                  <div>
                    <p className="overline mb-1">Response time</p>
                    <p className="text-aviana-ink font-serif text-[17px]">Within three days</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: contact form */}
          <form onSubmit={submit} className="lg:col-span-7 bg-aviana-cream rounded-[2rem] p-10 md:p-12 border border-aviana-border" data-testid="contact-form-card">
            {honeypot}
            <p className="overline mb-4">Send a message</p>
            <h2 className="font-display text-3xl md:text-4xl text-aviana-ink leading-tight mb-8">
              Write to <em className="italic font-serif gold-text">Holly.</em>
            </h2>

            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Your name" testid="cf-name">
                <input required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} data-testid="contact-name" />
              </Field>
              <Field label="Email" testid="cf-email">
                <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="contact-email" />
              </Field>
              <Field label="Phone (optional)" testid="cf-phone">
                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} data-testid="contact-phone" />
              </Field>
              <Field label="Subject" testid="cf-subject">
                <select value={form.subject} onChange={(e) => update("subject", e.target.value)} className={inputCls} data-testid="contact-subject">
                  {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Your message" testid="cf-message" className="mt-6">
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className={`${inputCls} resize-none`}
                placeholder="Share what's calling you, any questions, dates you're holding…"
                data-testid="contact-message"
              />
            </Field>

            <button type="submit" disabled={busy} className="btn-gold w-full mt-10" data-testid="contact-submit">
              {busy ? "Sending…" : <>Send Message <Send size={14} /></>}
            </button>
            <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted/80 mt-5 text-center">
              Holly personally responds within 3 days
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, testid, className = "" }) {
  return (
    <label className={`block ${className}`} data-testid={testid}>
      <span className="overline-plum block mb-2">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 text-aviana-ink outline-none transition-colors text-sm";
